import {
    AdvancedFilterGroupNode,
    AdvancedLogicalOperator,
    appendAdvancedFilterNode,
    removeAdvancedFilterNode,
    updateAdvancedFilterGroupNegated,
    updateAdvancedFilterGroupOperator,
} from '@/domain/advancedFilter';
import { Egenskapstype } from '@/domain/vegobjekter/Egenskapstype';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { UserState } from '@/state/UserState';
import { VegobjekttypeState } from '@/state';
import * as React from 'react';
import {
    AddButtons,
    AddActionKind,
    createNodeForAddAction,
} from './AdvancedFilterAddButtons';
import { AdvancedFilterNodeEditor } from './AdvancedFilterNodeEditor';

interface Props {
    root: AdvancedFilterGroupNode;
    group: AdvancedFilterGroupNode;
    groupDepth: number;
    isRoot?: boolean;
    label?: string;
    removableNodeId?: string;
    egenskapstyper: Egenskapstype[];
    vegobjekttypeData: VegobjekttypeState;
    datakatalogVegobjekttyper: Vegobjekttype[];
    errorsByNodeId: Record<string, string[]>;
    onDraftChange: (draft: AdvancedFilterGroupNode | null) => void;
    user: UserState;
    disableSuggestedValueFetch?: boolean;
}

export const AdvancedFilterGroupEditor: React.FC<Props> = ({
    root,
    group,
    groupDepth,
    isRoot = false,
    label,
    removableNodeId,
    egenskapstyper,
    vegobjekttypeData,
    datakatalogVegobjekttyper,
    errorsByNodeId,
    onDraftChange,
    user,
    disableSuggestedValueFetch = false,
}) => {
    const nodeDepth = groupDepth + 1;
    const groupErrors = errorsByNodeId[group.id] ?? [];
    const groupLevelClass = !isRoot
        ? ` c-advanced-filter__group--level-${Math.min(groupDepth, 4)}`
        : '';
    const groupModifier = isRoot ? ' c-advanced-filter__group--root' : '';

    const onAdd = React.useCallback(
        (kind: AddActionKind) => {
            onDraftChange(
                appendAdvancedFilterNode(root, group.id, createNodeForAddAction(kind))
            );
        },
        [group.id, onDraftChange, root]
    );

    const onOperatorChange = React.useCallback(
        (operator: AdvancedLogicalOperator) => {
            onDraftChange(updateAdvancedFilterGroupOperator(root, group.id, operator));
        },
        [group.id, onDraftChange, root]
    );

    const onRemove = React.useCallback(() => {
        onDraftChange(removeAdvancedFilterNode(root, removableNodeId ?? group.id));
    }, [group.id, onDraftChange, removableNodeId, root]);

    const onNegatedChange = React.useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const negated = event.target.value === 'without';
            onDraftChange(updateAdvancedFilterGroupNegated(root, group.id, negated));
        },
        [group.id, onDraftChange, root]
    );

    return (
        <div className={`c-advanced-filter__group${groupLevelClass}${groupModifier}`}>
            {(label || !isRoot) && (
                <div className="c-advanced-filter__group-header">
                    <div className="c-advanced-filter__group-header-main">
                        <div
                            className="c-advanced-filter__group-title"
                            title="Underfilter samler flere filtere sammen med en logisk operator og/eller"
                        >
                            <i
                                className="material-icons c-advanced-filter__node-kind-icon"
                                aria-hidden="true"
                            >
                                workspaces
                            </i>
                            <select
                                className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--negation"
                                value={group.negated ? 'without' : 'with'}
                                onChange={onNegatedChange}
                                aria-label="Velg om underfilteret skal finne treff med eller uten det som er valgt i underfilteret"
                                title="Velg med eller uten det som er valgt i underfilteret"
                            >
                                <option value="with">{label ?? 'Underfilter'}</option>
                                <option value="without">Underfilter uten</option>
                            </select>
                        </div>
                    </div>
                    {!isRoot && (
                        <button
                            type="button"
                            className="c-advanced-filter__remove-button"
                            onClick={onRemove}
                        >
                            Fjern
                        </button>
                    )}
                </div>
            )}

            {groupErrors.length > 0 && (
                <div className="c-advanced-filter__errors">
                    {groupErrors.map(error => (
                        <p key={error} className="c-advanced-filter__error">
                            {error}
                        </p>
                    ))}
                </div>
            )}

            <div className="c-advanced-filter__nodes">
                {group.children.map((child, index) => (
                    <React.Fragment key={child.id}>
                        <AdvancedFilterNodeEditor
                            root={root}
                            node={child}
                            nodeDepth={nodeDepth}
                            egenskapstyper={egenskapstyper}
                            vegobjekttypeData={vegobjekttypeData}
                            datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                            errorsByNodeId={errorsByNodeId}
                            onDraftChange={onDraftChange}
                            user={user}
                            disableSuggestedValueFetch={disableSuggestedValueFetch}
                        />
                        {index < group.children.length - 1 && (
                            <div
                                className="c-advanced-filter__connector"
                                role="checkbox"
                                aria-checked={group.operator === 'OR'}
                                aria-label={`Koble med ${group.operator === 'AND' ? 'og' : 'eller'}`}
                                title='Velg logisk operator for å koble filterene sammen. "og" betyr at begge filterene må være oppfylt, "eller" betyr at det holder at ett av filterene er oppfylt.'
                                tabIndex={0}
                                onClick={() =>
                                    onOperatorChange(
                                        group.operator === 'AND' ? 'OR' : 'AND'
                                    )
                                }
                                onKeyDown={e => {
                                    if (e.key === ' ') {
                                        e.preventDefault();
                                        onOperatorChange(
                                            group.operator === 'AND' ? 'OR' : 'AND'
                                        );
                                    }
                                }}
                            >
                                <span
                                    className={`c-advanced-filter__connector-option${
                                        group.operator === 'AND'
                                            ? ' c-advanced-filter__connector-option--selected'
                                            : ''
                                    }`}
                                >
                                    og
                                </span>
                                <span
                                    className={`c-advanced-filter__connector-option${
                                        group.operator === 'OR'
                                            ? ' c-advanced-filter__connector-option--selected'
                                            : ''
                                    }`}
                                >
                                    eller
                                </span>
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <AddButtons onAdd={onAdd} containerDepth={groupDepth} />
        </div>
    );
};
